"use client";
/**
 * Route guards for mode-specific screens.
 *
 * v1 had `AppModeGuard` doing this; v2 shipped without it, so every ordering
 * and account route stayed reachable on a digital-menu link.
 *
 * Both guards render children while the mode is still `unresolved`. Bouncing
 * during bootstrap would be worse than the thing we're preventing: a customer
 * mid-checkout on a slow config fetch would get thrown back to the menu.
 * `apiFetch`'s menu-only guard is the backstop for that window.
 */
import { Navigate } from "react-router-dom";
import type { ReactNode } from "react";

import { useAppMode } from "@/hooks/useAppMode";

/** Ordering + account routes: blocked on menu-only links. */
export function RequireOrdering({ children }: { children: ReactNode }) {
  const { isMenuOnly } = useAppMode();
  if (isMenuOnly) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** The shortlist: only exists on menu-only links. */
export function RequireMenuOnly({ children }: { children: ReactNode }) {
  const { isMenuOnly, isResolving } = useAppMode();
  if (!isMenuOnly && !isResolving) return <Navigate to="/" replace />;
  return <>{children}</>;
}
